/**
 * Map QuickBooks BillPayment rows onto CRM supplier bills for Forecast clearing.
 * Once QB has a BillPayment linked to the bill, treat txnDate as bank-cleared.
 */

export type QbBillPaymentLite = {
  txnDate?: string | Date | null;
  payType?: string | null;
  lines?: Array<{ billQuickbooksId?: string | null }>;
};

export function qbClearedAtByQbBillId(
  payments: QbBillPaymentLite[],
): Map<string, { clearedAt: Date; payType: string | null }> {
  const out = new Map<string, { clearedAt: Date; payType: string | null }>();
  for (const pay of payments) {
    const raw = pay.txnDate;
    let clearedAt: Date | null = null;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      clearedAt = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    } else if (typeof raw === 'string' && raw.trim()) {
      const d = new Date(`${raw.trim()}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        clearedAt = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      }
    }
    if (!clearedAt) continue;
    const payType = typeof pay.payType === 'string' ? pay.payType : null;
    for (const line of pay.lines || []) {
      const qbId = String(line.billQuickbooksId || '').trim();
      if (!qbId) continue;
      const prev = out.get(qbId);
      if (!prev || clearedAt.getTime() > prev.clearedAt.getTime()) {
        out.set(qbId, { clearedAt, payType });
      }
    }
  }
  return out;
}

export function applyQbClearingToSupplierBills<T extends {
  qbBillId?: string | null;
  qbClearedAt?: Date | null;
  paymentMethod?: string | null;
}>(
  bills: T[],
  clearByQbId: Map<string, { clearedAt: Date; payType: string | null }>,
): T[] {
  if (clearByQbId.size === 0) return bills;
  return bills.map((bill) => {
    const qbId = String(bill.qbBillId || '').trim();
    if (!qbId) return bill;
    const hit = clearByQbId.get(qbId);
    if (!hit) return bill;
    return {
      ...bill,
      qbClearedAt: bill.qbClearedAt || hit.clearedAt,
      qbPaymentDepositDate: (bill as { qbPaymentDepositDate?: Date | null }).qbPaymentDepositDate || hit.clearedAt,
      paymentMethod: bill.paymentMethod || hit.payType,
    };
  });
}
